import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, User, Smartphone, Globe, Shield } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();

  const settingsGroups = [
    {
      title: 'Device & User',
      items: [
        {
          icon: User,
          title: 'User Level',
          description: 'Manage your current restriction level',
          path: `/${serial}/settings/level`,
          color: 'bg-blue-500/10',
          iconColor: 'text-blue-500'
        },
      ],
    },
    {
      title: 'Content Controls',
      items: [
        {
          icon: Smartphone,
          title: 'Apps & Categories',
          description: 'Manage allowed and blocked app categories',
          path: `/${serial}/settings/apps`,
          color: 'bg-purple-500/10',
          iconColor: 'text-purple-500'
        },
        {
          icon: Globe,
          title: 'Browser Policies',
          description: 'Configure website restrictions and policies',
          path: `/${serial}/settings/browser`,
          color: 'bg-green-500/10',
          iconColor: 'text-green-500'
        },
        {
          icon: Shield,
          title: 'General Restrictions',
          description: 'Block GIFs, ads, and other content',
          path: `/${serial}/settings/general`,
          color: 'bg-orange-500/10',
          iconColor: 'text-orange-500'
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-muted/30 pb-24">
      {/* Header */}
      <div className="bg-background border-b border-border">
        <div className="px-5 pt-6 pb-4">
          <h1 className="text-[28px] font-bold tracking-tight text-foreground">Settings</h1>
        </div>
      </div>

      {/* Settings Groups */}
      <div className="px-4 pt-6 space-y-6">
        {settingsGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            <h2 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide px-4 mb-2">
              {group.title}
            </h2>
            <div className="bg-background rounded-xl overflow-hidden shadow-sm">
              {group.items.map((item, itemIndex) => {
                const Icon = item.icon;
                const isLast = itemIndex === group.items.length - 1;
                
                return (
                  <button
                    key={itemIndex}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center px-4 py-3.5 active:bg-muted/50 active:scale-[0.99] transition-all ${
                      !isLast ? 'border-b border-border' : ''
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center mr-3`}>
                      <Icon className={`w-5 h-5 ${item.iconColor}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-[15px] text-foreground">{item.title}</div>
                      <div className="text-[13px] text-muted-foreground/80 leading-tight mt-0.5">
                        {item.description}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground/40 ml-2" />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Settings;
